import React from "react";

import useScrollToTop from "../../../hooks/useScollToTop";
import { useNotification } from "../../../components/Notification/NotificationManager";
import { useUser } from "../../Home/UserContext";

import { useEventsJudoka } from "./hooks/useEventsJudoka";

import AdherentEventSelector from "./components/AdherentEventSelector";
import EventCard from "./components/EventCard";
import EventFilters from "./components/EventFilters";
import EventFormModal from "./components/EventFormModal";
import EventInvitationModal from "./components/EventInvitationModal";
import EventResultsModal from "./components/EventResultsModal";
import EventStats from "./components/EventStats";
import EventsEmptyState from "./components/EventsEmptyState";
import EventsLoadingState from "./components/EventsLoadingState";
import EventsPageHeader from "./components/EventsPageHeader";

function EventsJudokaPage() {
  useScrollToTop();

  const { addNotification } = useNotification();
  const { user } = useUser();

  const {
    adherents,
    selectedAdherent,
    selectedAdherentId,

    eventsByTime,
    filteredEvents,
    filters,
    resultsCount,

    form,

    isEventModalOpen,
    isEditMode,
    invitationEvent,
    resultsEvent,

    competitorsByEvent,
    loadingCompetitors,

    loading,
    eventsLoading,
    saving,

    selectAdherent,

    openCreateModal,
    openEditModal,
    closeEventModal,

    openInvitationModal,
    closeInvitationModal,

    openResultsModal,
    closeResultsModal,

    handleFilterChange,
    resetFilters,

    handleInputChange,
    handleSubmitEvent,
    handleDeleteEvent,
    handleStatusChange,

    fetchCompetitors,
    refreshEvents,
  } = useEventsJudoka({ addNotification });

  if (loading) return <EventsLoadingState />;

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-50 px-4 pb-16 pt-28 font-satoshi dark:bg-slate-950 sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(249,115,22,0.14),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(37,99,235,0.16),transparent_32%)]" />
      <div className="absolute inset-x-0 top-0 h-80 bg-slate-950" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-400/80 to-transparent" />

      <section className="relative mx-auto max-w-7xl">
        <EventsPageHeader onCreate={openCreateModal} />

        <AdherentEventSelector
          adherents={adherents}
          selectedId={selectedAdherentId}
          onSelect={selectAdherent}
        />

        <EventStats
          upcomingCount={eventsByTime.upcoming.length}
          pastCount={eventsByTime.past.length}
          resultsCount={resultsCount}
        />

        <EventFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onReset={resetFilters}
        />

        {eventsLoading && (
          <div className="grid gap-5 lg:grid-cols-2">
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="h-80 animate-pulse rounded-[2rem] bg-white shadow-xl dark:bg-white/[0.06]"
              />
            ))}
          </div>
        )}

        {!eventsLoading && filteredEvents.length === 0 && (
          <EventsEmptyState onCreate={openCreateModal} />
        )}

        {!eventsLoading && filteredEvents.length > 0 && (
          <div className="grid gap-6 lg:grid-cols-2">
            {filteredEvents.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                adherentId={selectedAdherent?.id}
                user={user}
                competitors={competitorsByEvent[event.id] || []}
                competitorsLoaded={Object.prototype.hasOwnProperty.call(
                  competitorsByEvent,
                  event.id,
                )}
                competitorsLoading={loadingCompetitors[event.id]}
                onFetchCompetitors={fetchCompetitors}
                onEdit={openEditModal}
                onDelete={handleDeleteEvent}
                onViewResults={openResultsModal}
                onSendInvitation={openInvitationModal}
                onStatusChange={handleStatusChange}
              />
            ))}
          </div>
        )}
      </section>

      <EventFormModal
        isOpen={isEventModalOpen}
        onClose={closeEventModal}
        form={form}
        onInputChange={handleInputChange}
        onSubmit={handleSubmitEvent}
        isEditMode={isEditMode}
        saving={saving}
      />

      <EventInvitationModal
        isOpen={Boolean(invitationEvent)}
        onClose={() => {
          closeInvitationModal();
          refreshEvents();
        }}
        event={invitationEvent}
      />

      <EventResultsModal event={resultsEvent} onClose={closeResultsModal} />
    </main>
  );
}

export default EventsJudokaPage;